// ============================================================================
// NOC 2021 resolver
// ============================================================================
// The OINP scores two things off the occupation code: the TEER category and the
// broad occupational category. Both are STRUCTURAL properties of the NOC 2021
// code itself, so we derive them arithmetically rather than keeping a lookup
// table that could drift:
//
//   - 1st digit -> broad occupational category
//   - 2nd digit -> TEER category
//
// Job-title SEARCH is a different matter and is served by widget-server. The
// official Statistics Canada dataset has ~28,000 example titles across 516 unit
// groups — and those examples are the whole point, because people type "PSW" and
// "truck driver", not "Nurse aides, orderlies and patient service associates".
// At ~900 KB that index has no business in a public widget bundle that ships at
// ~325 KB gzipped, so the search box calls GET /assessments/noc/search instead.
//
// The manual 5-digit path stays fully offline: resolveNoc() needs no network.
// ============================================================================

import { BROAD_CATEGORY_LABELS, isSkilledTradeNoc } from './rules';

const base_url = process.env.REACT_APP_API_BASE_URL || '';

export const NOC_LOOKUP_URL = 'https://noc.esdc.gc.ca/';
export const NOC_VERSION = 'NOC 2021 V1.0';

/** True for exactly five digits. */
export function isValidNocCode(code) {
  return typeof code === 'string' && /^\d{5}$/.test(code.trim());
}

/**
 * Resolve a 5-digit NOC 2021 code into its structural parts. No network, no
 * lookup table. Returns null for anything that is not a valid code.
 */
export function resolveNoc(code) {
  if (!isValidNocCode(code)) return null;
  const clean = String(code).trim();
  const broadCategory = Number(clean[0]);
  const teer = Number(clean[1]);

  return {
    code: clean,
    teer,
    broadCategory,
    broadCategoryLabel: BROAD_CATEGORY_LABELS.table[broadCategory] || `Category ${broadCategory}`,
    broadCategoryLabelVerified: Boolean(BROAD_CATEGORY_LABELS.table[broadCategory]),
    // Skilled trades get a lower language bar and a lower education bar.
    isSkilledTrade: isSkilledTradeNoc(clean),
  };
}

/**
 * Job-title search against the official NOC index, server-side.
 * Resolves to [] on any failure — the manual code-entry path is always available,
 * so a search outage must never block the step.
 *
 * @returns {Promise<Array<{code, title, matchedOn, teer, broadCategory}>>}
 */
export async function searchNocByTitle(query, { signal } = {}) {
  const q = String(query || '').trim();
  if (q.length < 2) return [];

  try {
    const response = await fetch(
      `${base_url}/assessments/noc/search?q=${encodeURIComponent(q)}&limit=8`,
      { signal }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data?.results) ? data.results : [];
  } catch (error) {
    // AbortError is the normal path when the user keeps typing.
    if (error?.name !== 'AbortError') {
      console.warn('[NOC] Search unavailable:', error?.message);
    }
    return [];
  }
}

/** Confirm a manually entered code against the official index (optional). */
export async function lookupNocCode(code) {
  if (!isValidNocCode(code)) return null;
  try {
    const response = await fetch(`${base_url}/assessments/noc/${String(code).trim()}`);
    if (!response.ok) return null;
    const data = await response.json();
    return data?.result || null;
  } catch {
    return null;
  }
}
